import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  UserSearch, Plus, Loader2, Search, ChevronRight, Trash2, X,
  Phone, Mail, Building2, Tag, ArrowRightLeft, MessageSquare,
  Upload, Filter, BarChart3, Users, Clock, CheckCircle2,
  GraduationCap, Columns, List, Eye, PhoneCall, MessagesSquare, Send,
  ClipboardList, Settings2, Save, GitBranch, Bot, Sparkles, ExternalLink, Facebook
} from "lucide-react";
import { AgentChat } from "../components/AgentChat";
import { CrmRoutinesPanel } from "../components/CrmRoutinesPanel";
import AircallCallButton from "../components/AircallCallButton";
import AircallCallHistory from "../components/AircallCallHistory";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";
import AIScoreBadge from "../components/entity-detail/AIScoreBadge";
import PartnerBadge from "../components/affiliate/PartnerBadge";
import MetaLeadsDialog from "../components/prospects/MetaLeadsDialog";
import AircallCallsDialog from "../components/prospects/AircallCallsDialog";

const STATUS_CONFIG = {
  nouveau: { label: "Nouveau", color: "#3b82f6", bg: "bg-blue-500/10 text-blue-400" },
  contacte: { label: "Contacte\u0301", color: "#8b5cf6", bg: "bg-purple-500/10 text-purple-400" },
  qualifie: { label: "Qualifie\u0301", color: "#f59e0b", bg: "bg-yellow-500/10 text-yellow-400" },
  proposition: { label: "Proposition", color: "#f97316", bg: "bg-orange-500/10 text-orange-400" },
  converti: { label: "Converti", color: "#22c55e", bg: "bg-green-500/10 text-green-400" },
  perdu: { label: "Perdu", color: "#ef4444", bg: "bg-red-500/10 text-red-400" },
};

const QUAL_CONFIG = {
  a_qualifier: { label: "A qualifier", color: "#64748b" },
  qualifie: { label: "Qualifie", color: "#22c55e" },
  non_qualifie: { label: "Non qualifie", color: "#ef4444" },
  en_attente: { label: "En attente", color: "#f59e0b" },
};

const SOURCE_LABELS = { manual: "Manuel", import: "Import", ghl: "Go High Level", meta: "Meta / Facebook", meta_lead_ads: "Meta Lead Ads", website: "Site web", referral: "Recommandation" };

const DEMARRER_OPTIONS = { dans_les_2_mois_: "Dans les 2 mois", le_trimestre_prochain: "Trimestre prochain", dans_6_mois: "Dans 6 mois", immediatement: "Immediatement" };

const STATUT_PRO_OPTIONS = {
  "je_suis_salarie_dans_l'automobile_ou_la_serrurerie": "Salarie auto/serrurerie",
  "je_suis_salarie_dans_un_autre_secteur_d'activite": "Salarie autre secteur",
  "je_dirige_une_activite_dans_l'automobile_ou_la_serrurerie": "Dirigeant auto/serrurerie",
  "ni_l'un_ni_l'autre": "Ni l'un ni l'autre",
};

const MODULE_OPTIONS = {
  "g1": "G1 - Prog. calculateur moteur",
  "g2": "G2 - Serrurerie automobile",
  "c1": "C1 - Formation complete (G1+G2)",
  "m1+": "M1+ - Outils reprogrammation",
  "m2": "M2 - Calibration/cartographie",
  "m3": "M3 - Programmation de cles",
  "m4": "M4 - Crochetage/decodage",
  "experts": "Modules Experts",
};

// Normalize GHL module value to key
function normalizeModule(raw) {
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower.includes("experts") || lower.includes("vag_mqb")) return "experts";
  if (lower.includes("c1")) return "c1";
  if (lower.includes("g2")) return "g2";
  if (lower.includes("g1")) return "g1";
  if (lower.includes("m1")) return "m1+";
  if (lower.includes("m2")) return "m2";
  if (lower.includes("m3")) return "m3";
  if (lower.includes("m4")) return "m4";
  return raw;
}

function normalizePro(raw) {
  if (!raw) return "";
  const lower = raw.toLowerCase().replace(/\u0026apos;/g, "'").replace(/[éèê]/g, "e").replace(/[àâ]/g, "a");
  if (lower.includes("dirige")) return "je_dirige_une_activite_dans_l'automobile_ou_la_serrurerie";
  if (lower.includes("salarie") && (lower.includes("automobile") || lower.includes("serrurerie"))) return "je_suis_salarie_dans_l'automobile_ou_la_serrurerie";
  if (lower.includes("salarie") && lower.includes("autre")) return "je_suis_salarie_dans_un_autre_secteur_d'activite";
  if (lower.includes("ni_l")) return "ni_l'un_ni_l'autre";
  return raw;
}

export default function ProspectPage() {
  const { axiosAuth } = useAuth();
  const { societeApi, currentSociete } = useSociete();
  const { t } = useTranslation();
  const api = societeApi();
  const [prospects, setProspects] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterModule, setFilterModule] = useState("");
  const [filterDemarrer, setFilterDemarrer] = useState("");
  const [filterStatutPro, setFilterStatutPro] = useState("");
  const [filterFinancement, setFilterFinancement] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showMetaLeads, setShowMetaLeads] = useState(false);
  const [showAircallCalls, setShowAircallCalls] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", company: "", source: "manual", notes: "", tags: [],
    quand_demarrer: "", statut_professionnel: "", module_interesse: "", financement_propre: "", raison_formation: "",
  });
  const [tagInput, setTagInput] = useState("");
  const [activityText, setActivityText] = useState("");
  const [activityType, setActivityType] = useState("note");

  // Questionnaire integration
  const [allQuestionnaires, setAllQuestionnaires] = useState([]);
  const [activeQstIds, setActiveQstIds] = useState([]);
  const [qstResponses, setQstResponses] = useState({});
  const [showConfig, setShowConfig] = useState(false);
  const [savingQst, setSavingQst] = useState(false);
  const [detailTab, setDetailTab] = useState("info");

  // Classeur & Agent
  const [activeClasseur, setActiveClasseur] = useState("all");
  const [showAgent, setShowAgent] = useState(false);
  const [showRoutines, setShowRoutines] = useState(false);
  const [lastRoutineResult, setLastRoutineResult] = useState(null);

  // Create form questionnaire
  const [createQstId, setCreateQstId] = useState("");
  const [createQstResponses, setCreateQstResponses] = useState({});

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (filterStatus) params.set("status", filterStatus);
      if (filterSource) params.set("source", filterSource);
      const [p, s, cfgRes, qstRes] = await Promise.all([
        api.get(`/prospects?${params}`),
        api.get("/prospects/stats"),
        api.get("/prospect-module/config").catch(() => ({ data: { active_questionnaires: [] } })),
        api.get("/prospect-questionnaires").catch(() => ({ data: [] })),
      ]);
      setProspects(p.data || []);
      setStats(s.data);
      setActiveQstIds(cfgRes.data?.active_questionnaires || []);
      setAllQuestionnaires(qstRes.data || []);
    } catch {}
    setLoading(false);
  }, [search, filterStatus, filterSource]);

  useEffect(() => { reload(); }, [search, filterStatus, filterSource, currentSociete]);

  // Client-side filters for questionnaire fields
  const filtered = prospects.filter(p => {
    // Classeur filter
    if (activeClasseur === "no_qst" && p.questionnaire_id) return false;
    if (activeClasseur !== "all" && activeClasseur !== "no_qst" && p.questionnaire_id !== activeClasseur) return false;
    if (filterModule && normalizeModule(p.module_interesse) !== filterModule) return false;
    if (filterDemarrer && p.quand_demarrer !== filterDemarrer) return false;
    if (filterStatutPro && normalizePro(p.statut_professionnel) !== filterStatutPro) return false;
    if (filterFinancement && p.financement_propre !== filterFinancement) return false;
    return true;
  });

  const createProspect = async () => {
    if (!form.first_name) return;
    setSaving(true);
    try {
      const payload = { ...form };
      if (createQstId) {
        payload.questionnaire_id = createQstId;
        payload.questionnaire_responses = { [createQstId]: createQstResponses };
      }
      await api.post("/prospects", payload);
      setShowCreate(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", source: "manual", notes: "", tags: [], quand_demarrer: "", statut_professionnel: "", module_interesse: "", financement_propre: "", raison_formation: "" });
      setCreateQstId("");
      setCreateQstResponses({});
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  const deleteProspect = async (pid) => {
    if (!window.confirm("Supprimer ce prospect ?")) return;
    await api.delete(`/prospects/${pid}`);
    if (showDetail?.prospect_id === pid) setShowDetail(null);
    reload();
  };

  const changeStatus = async (pid, status) => {
    await api.post(`/prospects/${pid}/status`, { status });
    const r = await api.get(`/prospects/${pid}`);
    setShowDetail(r.data);
    reload();
  };

  const addActivity = async () => {
    if (!showDetail || !activityText.trim()) return;
    await api.post(`/prospects/${showDetail.prospect_id}/activity`, { type: activityType, content: activityText });
    setActivityText("");
    setActivityType("note");
    const r = await api.get(`/prospects/${showDetail.prospect_id}`);
    setShowDetail(r.data);
  };

  const convertToCrm = async (pid) => {
    if (!window.confirm("Convertir ce prospect en opportunite CRM ?")) return;
    setSaving(true);
    try {
      await api.post(`/prospects/${pid}/convert`);
      const r = await api.get(`/prospects/${pid}`);
      setShowDetail(r.data);
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };

  // Questionnaire helpers
  const activeQuestionnaires = allQuestionnaires.filter(q => activeQstIds.includes(q.questionnaire_id));

  const openDetail = async (prospect) => {
    setShowDetail(prospect);
    setDetailTab("info");
    // Load saved responses
    const saved = prospect.questionnaire_responses || {};
    setQstResponses(saved);
  };

  const saveQstConfig = async (ids) => {
    await api.put("/prospect-module/config", { active_questionnaires: ids });
    setActiveQstIds(ids);
  };

  const saveQstResponses = async (qstId) => {
    if (!showDetail) return;
    setSavingQst(true);
    try {
      await api.put(`/prospects/${showDetail.prospect_id}/questionnaire-responses`, {
        questionnaire_id: qstId,
        responses: qstResponses[qstId] || {},
      });
    } catch (e) { console.error(e); }
    setSavingQst(false);
  };

  const updateQstResponse = (qstId, fieldId, value) => {
    setQstResponses(prev => ({
      ...prev,
      [qstId]: { ...(prev[qstId] || {}), [fieldId]: value },
    }));
  };

  const isFieldVisible = (qst, fieldId, responses) => {
    const resp = responses || {};
    // Check if this field is a "then_show" target of any condition
    const conditions = qst.conditions || [];
    const targetConditions = conditions.filter(c => (c.then_show_fields || []).includes(fieldId));
    if (targetConditions.length === 0) return true; // no conditions affect this field
    // If any condition targeting this field is met, show it
    return targetConditions.some(c => {
      const val = resp[c.if_field_id] || "";
      if (c.operator === "equals") return val === c.value;
      if (c.operator === "not_equals") return val !== c.value;
      if (c.operator === "contains") return val.includes(c.value);
      if (c.operator === "not_empty") return val.length > 0;
      return true;
    });
  };

  // GHL Excel import handler
  const handleImport = async (file) => {
    if (!file) return;
    setSaving(true);
    try {
      const text = await file.text();
      const lines = text.split("\n").filter(l => l.trim());
      if (lines.length < 2) { alert("Fichier vide"); setSaving(false); return; }
      const sep = lines[0].includes("\t") ? "\t" : lines[0].includes(";") ? ";" : ",";
      const headers = lines[0].split(sep).map(h => h.trim().replace(/['"]/g, "").toLowerCase());
      const leads = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(sep).map(c => c.trim().replace(/^['"]|['"]$/g, ""));
        if (cols.length < 3) continue;
        const row = {};
        headers.forEach((h, j) => { row[h] = cols[j] || ""; });
        // Map GHL Excel columns (shifted format)
        const lead = {
          first_name: (row["name"] || "").split(" ")[0] || "",
          last_name: (row["name"] || "").split(" ").slice(1).join(" ") || "",
          phone: row["personne"] || row["phone"] || "",
          email: row["qualification"] || row["email"] || "",
          quand_demarrer: row["action"] || "",
          statut_professionnel: row["statut"] || "",
          budget_equipements: row["période - start"] || row["periode - start"] || "",
          raison_formation: row["période - end"] || row["periode - end"] || "",
          financement_propre: row["phone"] && row["phone"].match(/^(oui|non)/) ? row["phone"] : "",
          module_interesse: row["email"] && row["email"].match(/^[a-z]/) && !row["email"].includes("@") ? row["email"] : "",
          outils_reprog: row["quand_souhaitez_vous_démarrer_votre_formation_?"] || row["quand_souhaitez"] || "",
          demarrer_activite: row["a_quel_moment_de_la_journée_préférez_vous_être_recontacté(e)"] || row["a_quel_moment"] || "",
          platform: row["etes_vous_salarié_ou_indépendant_?"] || row["platform"] || "",
          created_time: row["created_time"] || "",
          source: "ghl",
        };
        // Fix: if email looks like a module choice, use the qualification column instead
        if (lead.email && !lead.email.includes("@")) {
          lead.module_interesse = lead.email;
          lead.email = row["qualification"] || "";
        }
        if (lead.first_name || lead.email) leads.push(lead);
      }
      if (!leads.length) { alert("Aucun lead valide"); setSaving(false); return; }
      const r = await api.post("/prospects/import", { leads, source: "ghl" });
      alert(`${r.data.imported} prospect(s) importe(s), ${r.data.skipped} ignore(s) (doublons)`);
      setShowImport(false);
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Erreur d'import"); }
    setSaving(false);
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(f => ({ ...f, tags: [...f.tags, tagInput.trim()] }));
      setTagInput("");
    }
  };
  const removeTag = (t) => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== t) }));

  const activeFilters = [filterModule, filterDemarrer, filterStatutPro, filterFinancement].filter(Boolean).length;

  // Compute questionnaire fields for table columns based on active classeur
  const activeQstDef = activeClasseur !== "all" && activeClasseur !== "no_qst" ? allQuestionnaires.find(q => q.questionnaire_id === activeClasseur) : null;
  const qstColumns = activeQstDef ? (activeQstDef.fields || []) : [];

  return (
    <div className="p-4 md:p-6 max-w-[1800px] mx-auto" data-testid="prospect-page">
      <PageHeader
        icon={<UserSearch />}
        title={t("prospects.title") || "Prospects"}
        description={stats ? `${stats.total} prospect${stats.total > 1 ? 's' : ''} dans le pipeline` : "Gestion des prospects et conversion"}
        testId="prospect-header"
        actions={(
          <>
            <Button onClick={() => { setShowRoutines(!showRoutines); if (showAgent) setShowAgent(false); }} variant="outline" className={`border-white/10 text-slate-300 hover:bg-white/5 text-sm ${showRoutines ? "bg-[#f7c935]/10 border-[#f7c935]/30 text-[#f7c935]" : ""}`} data-testid="prospect-routines-btn">
              <BarChart3 className="w-4 h-4 mr-1" /> Routines IA CRM
            </Button>
            <Button onClick={() => { setShowAgent(!showAgent); if (showRoutines) setShowRoutines(false); }} variant="outline" className={`border-white/10 text-slate-300 hover:bg-white/5 text-sm ${showAgent ? "bg-blue-500/10 border-blue-500/30 text-blue-400" : ""}`} data-testid="prospect-agent-btn">
              <Bot className="w-4 h-4 mr-1" /> Agent CRM
            </Button>
            <Button onClick={() => setShowConfig(true)} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5 text-sm" data-testid="config-questionnaires-btn">
              <Settings2 className="w-4 h-4 mr-1" /> Questionnaires {activeQstIds.length > 0 && `(${activeQstIds.length})`}
            </Button>
            <Button onClick={() => setShowMetaLeads(true)} variant="outline" className="border-[#1877f2]/30 text-[#5a9df8] hover:bg-[#1877f2]/10 text-sm" data-testid="meta-leads-btn">
              <Facebook className="w-4 h-4 mr-1" /> Leads Meta
            </Button>
            <Button onClick={() => setShowAircallCalls(true)} variant="outline" className="border-[#00b388]/30 text-[#2ecfa5] hover:bg-[#00b388]/10 text-sm" data-testid="aircall-calls-btn">
              <PhoneCall className="w-4 h-4 mr-1" /> Appels Aircall
            </Button>
            <Button onClick={() => setShowImport(true)} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5 text-sm" data-testid="import-prospects-btn">
              <Upload className="w-4 h-4 mr-1" /> Import
            </Button>
            <Button onClick={() => setShowCreate(true)} className="bg-[#3b82f6] text-white hover:bg-[#2563eb] font-bold text-sm" data-testid="create-prospect-btn">
              <Plus className="w-4 h-4 mr-1" /> Prospect
            </Button>
          </>
        )}
      />

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
          {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
            <button key={key} onClick={() => setFilterStatus(filterStatus === key ? "" : key)}
              className={`rounded-xl p-3 text-center transition-all duration-200 border ${filterStatus === key ? "border-white/20 bg-white/5 scale-[1.02]" : "border-white/[0.06] bg-[#14101c] hover:border-white/10 hover:-translate-y-0.5"}`}
              data-testid={`stat-${key}`}>
              <div className="text-xl font-black font-heading" style={{ color: cfg.color }}>{stats.by_status?.[key] || 0}</div>
              <div className="text-[10px] text-[#888194] uppercase tracking-wider font-semibold">{cfg.label}</div>
            </button>
          ))}
        </div>
      )}

      {/* Classeur tabs (one per questionnaire + "Tous") */}
      {activeQuestionnaires.length > 0 && (
        <div className="flex gap-1.5 mb-4 overflow-x-auto pb-1" data-testid="classeur-tabs">
          <button onClick={() => setActiveClasseur("all")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${activeClasseur === "all" ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] text-slate-400 border border-white/5 hover:border-white/10"}`}
            data-testid="classeur-all">
            <Users className="w-3 h-3" /> Tous ({prospects.length})
          </button>
          {activeQuestionnaires.map(q => {
            const count = prospects.filter(p => p.questionnaire_id === q.questionnaire_id).length;
            return (
              <button key={q.questionnaire_id} onClick={() => setActiveClasseur(q.questionnaire_id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${activeClasseur === q.questionnaire_id ? "bg-[#8b5cf6] text-white" : "bg-[#14101c] text-slate-400 border border-white/5 hover:border-white/10"}`}
                data-testid={`classeur-${q.questionnaire_id}`}>
                <ClipboardList className="w-3 h-3" /> {q.name} ({count})
              </button>
            );
          })}
          <button onClick={() => setActiveClasseur("no_qst")}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${activeClasseur === "no_qst" ? "bg-slate-500 text-white" : "bg-[#14101c] text-slate-400 border border-white/5 hover:border-white/10"}`}
            data-testid="classeur-none">
            Sans formulaire ({prospects.filter(p => !p.questionnaire_id).length})
          </button>
          {lastRoutineResult && (
            <button onClick={() => setActiveClasseur("routine_ia")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${activeClasseur === "routine_ia" ? "bg-[#f7c935] text-[#14101c]" : "bg-[#14101c] text-[#f7c935] border border-[#f7c935]/20 hover:border-[#f7c935]/40"}`}
              data-testid="classeur-routine-ia">
              <Sparkles className="w-3 h-3" /> Routines IA
            </button>
          )}
        </div>
      )}

      {/* Filters bar */}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            className="pl-9 bg-[#14101c] border-white/10 text-white text-sm" data-testid="search-prospects" />
        </div>
        <Button variant="outline" onClick={() => setShowFilters(!showFilters)}
          className={`border-white/10 text-sm ${activeFilters > 0 ? "text-[#3b82f6] border-[#3b82f6]/30" : "text-slate-400"}`} data-testid="toggle-filters">
          <Filter className="w-4 h-4 mr-1" /> Filtres {activeFilters > 0 && `(${activeFilters})`}
        </Button>
        {(filterStatus || filterSource || activeFilters > 0) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(""); setFilterSource(""); setFilterModule(""); setFilterDemarrer(""); setFilterStatutPro(""); setFilterFinancement(""); }}
            className="text-slate-400 hover:text-white text-xs"><X className="w-3 h-3 mr-1" /> Reset</Button>
        )}
      </div>

      {/* Conditional filters panel */}
      {showFilters && (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 mb-4 grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="filters-panel">
          <div>
            <label className="text-[10px] text-slate-500 uppercase mb-1 block">Module</label>
            <select value={filterModule} onChange={e => setFilterModule(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs">
              <option value="">Tous</option>
              {Object.entries(MODULE_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase mb-1 block">Delai demarrage</label>
            <select value={filterDemarrer} onChange={e => setFilterDemarrer(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs">
              <option value="">Tous</option>
              {Object.entries(DEMARRER_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase mb-1 block">Statut pro</label>
            <select value={filterStatutPro} onChange={e => setFilterStatutPro(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs">
              <option value="">Tous</option>
              {Object.entries(STATUT_PRO_OPTIONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase mb-1 block">Financement propre</label>
            <select value={filterFinancement} onChange={e => setFilterFinancement(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs">
              <option value="">Tous</option>
              <option value="oui">Oui</option>
              <option value="non">Non</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase mb-1 block">Source</label>
            <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs">
              <option value="">Toutes</option>
              {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>
      )}

      {/* Routine IA Classeur View */}
      {activeClasseur === "routine_ia" && lastRoutineResult ? (
        <RoutineResultClasseur result={lastRoutineResult} />
      ) : loading ? (
        <ListSkeleton items={6} testId="prospect-loading-skeleton" />
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#888194]">
          <UserSearch className="w-14 h-14 mx-auto mb-4 text-[#461d8a]" />
          <p className="font-heading text-lg text-white/70">Aucun prospect</p>
          <p className="text-xs mt-1">Importez vos leads GHL ou creez un prospect manuellement</p>
        </div>
      ) : (
        <div className="bg-[#14101c] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="prospects-table-wrapper">
          {/* Classeur header when a specific questionnaire is selected */}
          {activeQstDef && (
            <div className="px-5 py-2.5 border-b border-white/[0.06] bg-[#0b0811]/60 backdrop-blur-xl flex items-center gap-2.5">
              <ClipboardList className="w-4 h-4 text-[#8b5cf6]" />
              <span className="text-sm font-heading font-semibold text-white">{activeQstDef.name}</span>
              <span className="text-[10px] text-[#888194]">{qstColumns.length} questions</span>
              <span className="text-[10px] text-[#888194] ml-auto">{filtered.length} prospect(s)</span>
            </div>
          )}
          <div className="overflow-x-auto w-full">
            <table className="w-full min-w-max text-left" data-testid="prospects-table">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="px-4 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider sticky left-0 bg-[#14101c] z-10" style={{ minWidth: "180px" }}>Nom</th>
                  <th className="px-3 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: "160px" }}>Contact</th>
                  <th className="px-3 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: "90px" }}>Date</th>
                  {qstColumns.length > 0 ? (
                    qstColumns.map(f => (
                      <th key={f.field_id || f.id} className="px-3 py-3 text-[10px] text-[#8b5cf6] uppercase font-bold tracking-wider whitespace-nowrap" style={{ minWidth: "150px" }}>
                        <span title={f.label}>{f.label?.length > 25 ? f.label.slice(0, 25) + "..." : f.label}</span>
                      </th>
                    ))
                  ) : (
                    <>
                      <th className="px-3 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: "100px" }}>Source</th>
                      <th className="px-3 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: "140px" }}>Formulaire</th>
                    </>
                  )}
                  <th className="px-3 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider" style={{ minWidth: "100px" }}>Statut</th>
                  <th className="px-3 py-3 text-[10px] text-[#888194] uppercase font-bold tracking-wider w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.nouveau;
                  const qstResp = activeQstDef ? ((p.questionnaire_responses || {})[activeQstDef.questionnaire_id] || {}) : {};
                  return (
                    <tr key={p.prospect_id} onClick={() => openDetail(p)}
                      className="border-b border-white/[0.03] hover:bg-white/[0.02] cursor-pointer transition-colors duration-150 group"
                      data-testid={`prospect-row-${p.prospect_id}`}>
                      <td className="px-4 py-3 sticky left-0 bg-[#14101c] z-10 group-hover:bg-[#1c1725]" style={{ transition: "background 0.15s" }}>
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                            style={{ background: `${sc.color}12`, color: sc.color, border: `1px solid ${sc.color}25` }}>
                            {(p.first_name || "?")[0].toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs font-semibold text-white truncate max-w-[140px]">{p.first_name} {p.last_name}</div>
                            {p.company && <div className="text-[9px] text-[#888194]">{p.company}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="text-[10px] text-[#d1cedb] truncate max-w-[150px]">{p.email}</div>
                        <div className="text-[10px] text-[#888194]">{p.phone}</div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-white/[0.04] text-[#888194] whitespace-nowrap">{p.created_at ? new Date(p.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "2-digit" }) : "-"}</span>
                      </td>
                      {qstColumns.length > 0 ? (
                        qstColumns.map(f => {
                          const fid = f.field_id || f.id;
                          const val = qstResp[fid] || "";
                          return (
                            <td key={fid} className="px-3 py-3">
                              {val ? (
                                <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#461d8a]/10 text-[#c4b5fd] whitespace-nowrap max-w-[140px] truncate inline-block border border-[#461d8a]/15" title={val}>
                                  {val.length > 30 ? val.slice(0, 30) + "..." : val}
                                </span>
                              ) : (
                                <span className="text-[10px] text-[#888194]/40">-</span>
                              )}
                            </td>
                          );
                        })
                      ) : (
                        <>
                          <td className="px-3 py-3"><span className="text-[10px] px-2 py-0.5 rounded-md bg-blue-500/8 text-blue-400 border border-blue-500/15 whitespace-nowrap">{SOURCE_LABELS[p.source] || p.source || "-"}</span></td>
                          <td className="px-3 py-3">
                            {p.questionnaire_id ? (
                              <span className="text-[10px] px-2 py-0.5 rounded-md bg-[#8b5cf6]/10 text-[#8b5cf6] border border-[#8b5cf6]/15">{(allQuestionnaires.find(q => q.questionnaire_id === p.questionnaire_id)?.name || "Formulaire").slice(0, 25)}</span>
                            ) : <span className="text-[10px] text-[#888194]/40">-</span>}
                          </td>
                        </>
                      )}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${sc.bg}`}>{sc.label}</span>
                          {p.ai_evaluation && (
                            <AIScoreBadge tier={p.ai_evaluation.tier} score={p.ai_evaluation.score} size="sm" testId={`row-ai-${p.prospect_id}`} />
                          )}
                          {p.partner_attribution?.partner_id && (
                            <PartnerBadge attribution={p.partner_attribution} compact />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1">
                          <button onClick={e => { e.stopPropagation(); deleteProspect(p.prospect_id); }}
                            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-red-500/10 rounded transition-opacity"><Trash2 className="w-3 h-3 text-red-400" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2.5 border-t border-white/[0.06] text-[10px] text-[#888194] flex items-center justify-between">
            <span>{filtered.length} prospect(s) {filtered.length !== prospects.length && `sur ${prospects.length}`}</span>
            {qstColumns.length > 0 && <span className="text-[#8b5cf6]">{qstColumns.length} colonnes questionnaire</span>}
          </div>
        </div>
      )}

      {/* Detail Dialog */}
      {showDetail && (
        <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
          <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold flex items-center gap-2">
                <UserSearch className="w-5 h-5 text-[#3b82f6]" /> {showDetail.first_name} {showDetail.last_name}
                {showDetail.ai_evaluation && (
                  <AIScoreBadge tier={showDetail.ai_evaluation.tier} score={showDetail.ai_evaluation.score} size="sm" testId="dialog-ai-score" />
                )}
                <Link
                  to={`/admin/prospect/${showDetail.prospect_id}`}
                  className="ml-auto inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-[#f7c935]/15 text-[#f7c935] hover:bg-[#f7c935]/25 border border-[#f7c935]/30 font-semibold"
                  data-testid="open-full-detail-btn"
                >
                  <ExternalLink className="w-3 h-3" /> Fiche complète
                </Link>
              </DialogTitle>
            </DialogHeader>

            {/* Status + actions */}
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${(STATUS_CONFIG[showDetail.status] || STATUS_CONFIG.nouveau).bg}`}>
                {(STATUS_CONFIG[showDetail.status] || STATUS_CONFIG.nouveau).label}
              </span>
              <div className="flex gap-1 ml-auto flex-wrap">
                {["nouveau", "contacte", "qualifie", "proposition"].map(st => (
                  <button key={st} onClick={() => changeStatus(showDetail.prospect_id, st)}
                    disabled={showDetail.status === st}
                    className={`text-[10px] px-2 py-1 rounded-lg transition-colors ${showDetail.status === st ? "bg-white/10 text-white" : "bg-[#0b0811] text-slate-400 hover:text-white border border-white/5"}`}>
                    {STATUS_CONFIG[st].label}
                  </button>
                ))}
                {showDetail.status !== "converti" && (
                  <button onClick={() => convertToCrm(showDetail.prospect_id)}
                    className="text-[10px] px-2 py-1 rounded-lg bg-green-500/10 text-green-400 hover:bg-green-500/20 border border-green-500/20 flex items-center gap-1"
                    data-testid="convert-to-crm-btn">
                    <ArrowRightLeft className="w-3 h-3" /> CRM
                  </button>
                )}
                <button onClick={() => changeStatus(showDetail.prospect_id, "perdu")}
                  className="text-[10px] px-2 py-1 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 ml-1">Perdu</button>
              </div>
            </div>

            {/* Tabs: Info + Questionnaires + Timeline */}
            <Tabs value={detailTab} onValueChange={setDetailTab}>
              <TabsList className="bg-[#0b0811] border border-white/5 w-full justify-start flex-wrap h-auto gap-0.5 p-1">
                <TabsTrigger value="info" className="data-[state=active]:bg-[#3b82f6] data-[state=active]:text-white text-xs" data-testid="tab-info">
                  <UserSearch className="w-3 h-3 mr-1" /> Fiche
                </TabsTrigger>
                {activeQuestionnaires.map(q => (
                  <TabsTrigger key={q.questionnaire_id} value={`qst_${q.questionnaire_id}`} className="data-[state=active]:bg-[#8b5cf6] data-[state=active]:text-white text-xs" data-testid={`tab-qst-${q.questionnaire_id}`}>
                    <ClipboardList className="w-3 h-3 mr-1" /> {q.name?.length > 18 ? q.name.slice(0, 18) + "..." : q.name}
                  </TabsTrigger>
                ))}
                <TabsTrigger value="timeline" className="data-[state=active]:bg-[#f7c935] data-[state=active]:text-[#14101c] text-xs" data-testid="tab-timeline">
                  <Clock className="w-3 h-3 mr-1" /> Timeline
                </TabsTrigger>
              </TabsList>

              {/* Info Tab */}
              <TabsContent value="info" className="space-y-4 mt-3">
                {/* Contact info with badges */}
                <div className="grid grid-cols-2 gap-3">
                  {showDetail.email && (
                    <div className="bg-[#0b0811] rounded-lg p-3">
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 mb-1"><Mail className="w-3 h-3" /> Email</div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400 inline-block">{showDetail.email}</span>
                    </div>
                  )}
                  {showDetail.phone && (
                    <div className="bg-[#0b0811] rounded-lg p-3">
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 mb-1"><Phone className="w-3 h-3" /> Telephone</div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 inline-block">{showDetail.phone}</span>
                        <AircallCallButton api={api} prospect={showDetail} onCalled={() => openDetail(showDetail)} />
                      </div>
                    </div>
                  )}
                  {showDetail.company && (
                    <div className="bg-[#0b0811] rounded-lg p-3">
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 mb-1"><Building2 className="w-3 h-3" /> Entreprise</div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-purple-500/10 text-purple-400 inline-block">{showDetail.company}</span>
                    </div>
                  )}
                  <div className="bg-[#0b0811] rounded-lg p-3">
                    <div className="text-[10px] text-slate-500 flex items-center gap-1 mb-1"><Tag className="w-3 h-3" /> Source</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 inline-block">{SOURCE_LABELS[showDetail.source] || showDetail.source} {showDetail.platform && `(${showDetail.platform})`}</span>
                  </div>
                </div>

                {/* Legacy questionnaire answers */}
                {(showDetail.module_interesse || showDetail.quand_demarrer || showDetail.statut_professionnel || showDetail.raison_formation) && (
                  <div className="bg-[#0b0811] rounded-xl p-4">
                    <h4 className="text-xs font-bold mb-3 flex items-center gap-2 text-[#8b5cf6]"><GraduationCap className="w-4 h-4" /> Questionnaire formation</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {showDetail.module_interesse && <div><div className="text-[9px] text-slate-600 uppercase">Module interesse</div><div className="text-xs text-slate-300 mt-0.5">{MODULE_OPTIONS[normalizeModule(showDetail.module_interesse)] || showDetail.module_interesse}</div></div>}
                      {showDetail.quand_demarrer && <div><div className="text-[9px] text-slate-600 uppercase">Delai demarrage</div><div className="text-xs text-slate-300 mt-0.5">{DEMARRER_OPTIONS[showDetail.quand_demarrer] || showDetail.quand_demarrer}</div></div>}
                      {showDetail.statut_professionnel && <div><div className="text-[9px] text-slate-600 uppercase">Statut professionnel</div><div className="text-xs text-slate-300 mt-0.5">{STATUT_PRO_OPTIONS[normalizePro(showDetail.statut_professionnel)] || showDetail.statut_professionnel}</div></div>}
                      {showDetail.raison_formation && <div className="col-span-2"><div className="text-[9px] text-slate-600 uppercase">Raison formation</div><div className="text-xs text-slate-300 mt-0.5">{showDetail.raison_formation}</div></div>}
                      {showDetail.financement_propre && <div><div className="text-[9px] text-slate-600 uppercase">Financement propre</div><div className="text-xs text-slate-300 mt-0.5">{showDetail.financement_propre}</div></div>}
                      {showDetail.budget_equipements && <div><div className="text-[9px] text-slate-600 uppercase">Budget equipements</div><div className="text-xs text-slate-300 mt-0.5">{showDetail.budget_equipements}</div></div>}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {showDetail.notes && <div className="bg-[#0b0811] rounded-lg p-3"><div className="text-[10px] text-slate-500 mb-1">Notes</div><div className="text-xs text-slate-300 whitespace-pre-wrap">{showDetail.notes}</div></div>}

                {/* Tags */}
                {(showDetail.tags || []).length > 0 && (
                  <div className="flex gap-1 flex-wrap">
                    {showDetail.tags.map((t, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#3b82f6]/10 text-[#3b82f6]">{t}</span>)}
                  </div>
                )}

                {/* Quick actions */}
                <div className="flex gap-2 pt-2 border-t border-white/5">
                  <Button onClick={() => deleteProspect(showDetail.prospect_id)} variant="outline" size="sm" className="border-red-500/20 text-red-400 hover:bg-red-500/10 text-xs" data-testid="delete-prospect-btn">
                    <Trash2 className="w-3 h-3 mr-1" /> Supprimer
                  </Button>
                </div>
              </TabsContent>

              {/* Questionnaire Tabs */}
              {activeQuestionnaires.map(qst => {
                const qstId = qst.questionnaire_id;
                const responses = qstResponses[qstId] || {};
                const sections = {};
                (qst.fields || []).forEach(f => {
                  const sec = f.section || "General";
                  if (!sections[sec]) sections[sec] = [];
                  sections[sec].push(f);
                });
                return (
                  <TabsContent key={qstId} value={`qst_${qstId}`} className="space-y-4 mt-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-bold" style={{ color: "#8b5cf6" }}>{qst.name}</h3>
                        {qst.description && <p className="text-[10px] text-slate-500 mt-0.5">{qst.description}</p>}
                      </div>
                      <Button onClick={() => saveQstResponses(qstId)} disabled={savingQst} size="sm" className="bg-[#8b5cf6] text-white hover:bg-[#7c3aed] text-xs" data-testid={`save-qst-${qstId}`}>
                        {savingQst ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Enregistrer
                      </Button>
                    </div>
                    {qst.conditions?.length > 0 && (
                      <div className="flex items-center gap-1 text-[9px] text-slate-600"><GitBranch className="w-3 h-3" /> {qst.conditions.length} condition(s) active(s)</div>
                    )}
                    {Object.entries(sections).map(([secName, secFields]) => (
                      <div key={secName} className="bg-[#0b0811] rounded-xl p-4">
                        <h4 className="text-xs font-bold mb-3 text-slate-400 uppercase tracking-wide">{secName}</h4>
                        <div className="grid grid-cols-2 gap-3">
                          {secFields.map(field => {
                            const fid = field.field_id || field.id;
                            if (!isFieldVisible(qst, fid, responses)) return null;
                            return (
                              <div key={fid} className={field.type === "textarea" ? "col-span-2" : ""} data-testid={`qst-field-${fid}`}>
                                <label className="text-[10px] text-slate-500 mb-1 block flex items-center gap-1">
                                  {field.label} {field.required && <span className="text-red-400">*</span>}
                                </label>
                                {(field.type === "text" || field.type === "email" || field.type === "phone" || field.type === "number") && (
                                  <Input type={field.type === "number" ? "number" : "text"} value={responses[fid] || ""} onChange={e => updateQstResponse(qstId, fid, e.target.value)}
                                    placeholder={field.placeholder || ""} className="bg-[#14101c] border-white/10 text-white text-xs h-8" />
                                )}
                                {field.type === "textarea" && (
                                  <Textarea value={responses[fid] || ""} onChange={e => updateQstResponse(qstId, fid, e.target.value)}
                                    placeholder={field.placeholder || ""} rows={2} className="bg-[#14101c] border-white/10 text-white text-xs" />
                                )}
                                {field.type === "select" && (
                                  <Select value={responses[fid] || ""} onValueChange={v => updateQstResponse(qstId, fid, v)}>
                                    <SelectTrigger className="bg-[#14101c] border-white/10 text-white text-xs h-8"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                                    <SelectContent className="bg-[#14101c] border-white/10 text-white">
                                      {(field.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                )}
                                {field.type === "checkbox" && (
                                  <div className="flex flex-wrap gap-2 mt-1">
                                    {(field.options || []).map(opt => {
                                      const selected = (responses[fid] || "").split(",").map(s => s.trim()).includes(opt);
                                      return (
                                        <button key={opt} onClick={() => {
                                          const current = (responses[fid] || "").split(",").map(s => s.trim()).filter(Boolean);
                                          const next = selected ? current.filter(x => x !== opt) : [...current, opt];
                                          updateQstResponse(qstId, fid, next.join(", "));
                                        }}
                                          className={`text-[10px] px-2 py-1 rounded-lg border transition-colors ${selected ? "bg-[#8b5cf6]/10 text-[#8b5cf6] border-[#8b5cf6]/30" : "bg-white/5 text-slate-500 border-white/5 hover:border-white/10"}`}>
                                          {opt}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                                {field.type === "date" && (
                                  <Input type="date" value={responses[fid] || ""} onChange={e => updateQstResponse(qstId, fid, e.target.value)}
                                    className="bg-[#14101c] border-white/10 text-white text-xs h-8" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </TabsContent>
                );
              })}

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="space-y-4 mt-3">
                <AircallCallHistory api={api} prospectId={showDetail.prospect_id} />
                <div className="flex gap-2 mb-3">
                  <select value={activityType} onChange={e => setActivityType(e.target.value)}
                    className="bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs w-24" data-testid="activity-type-select">
                    <option value="note">Note</option>
                    <option value="call">Appel</option>
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                  <Input value={activityText} onChange={e => setActivityText(e.target.value)} placeholder={activityType === "call" ? "Resume de l'appel..." : activityType === "email" ? "Objet / resume email..." : activityType === "sms" ? "Contenu SMS..." : "Ajouter une note..."}
                    className="bg-[#0b0811] border-white/10 text-white text-xs flex-1" data-testid="activity-input"
                    onKeyDown={e => e.key === "Enter" && addActivity()} />
                  <Button onClick={addActivity} size="sm" className="bg-[#3b82f6] text-white hover:bg-[#2563eb] text-xs" data-testid="add-activity-btn">
                    <Send className="w-3 h-3" />
                  </Button>
                </div>
                {(showDetail.activities || []).length === 0 ? (
                  <p className="text-xs text-slate-500 py-2">Aucune activite</p>
                ) : (
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                    {[...(showDetail.activities || [])].reverse().map((a, i) => {
                      const typeConfig = {
                        note: { icon: MessageSquare, color: "blue", label: "Note" },
                        call: { icon: PhoneCall, color: "green", label: "Appel" },
                        email: { icon: Mail, color: "purple", label: "Email" },
                        sms: { icon: MessagesSquare, color: "cyan", label: "SMS" },
                        status_change: { icon: ArrowRightLeft, color: "yellow", label: "Statut" },
                        conversion: { icon: CheckCircle2, color: "green", label: "Conversion" },
                        import: { icon: Upload, color: "slate", label: "Import" },
                      };
                      const tc = typeConfig[a.type] || typeConfig.note;
                      const IconComp = tc.icon;
                      return (
                        <div key={i} className="bg-[#0b0811] rounded-lg p-2.5 flex items-start gap-2">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-${tc.color}-500/10`}>
                            <IconComp className={`w-3 h-3 text-${tc.color}-400`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full bg-${tc.color}-500/10 text-${tc.color}-400 font-medium`}>{tc.label}</span>
                              {a.by_name && <span className="text-[9px] text-slate-500">{a.by_name}</span>}
                            </div>
                            <div className="text-xs text-slate-300">{a.content || `${a.from_status || ""} → ${a.to_status || ""}`}</div>
                            <div className="text-[9px] text-slate-600 mt-0.5">{a.at && new Date(a.at).toLocaleString("fr-FR")}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}

      {/* Questionnaire Config Dialog */}
      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Settings2 className="w-5 h-5" /> Configurer les questionnaires</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-400 mt-2">Selectionnez les questionnaires du Builder a afficher dans la fiche prospect.</p>
          <div className="space-y-2 mt-4 max-h-[40vh] overflow-y-auto">
            {allQuestionnaires.length === 0 ? (
              <p className="text-xs text-slate-500 py-4 text-center">Aucun questionnaire cree. Allez dans le Builder pour en creer.</p>
            ) : allQuestionnaires.map(q => (
              <button key={q.questionnaire_id} onClick={() => {
                const ids = activeQstIds.includes(q.questionnaire_id)
                  ? activeQstIds.filter(id => id !== q.questionnaire_id)
                  : [...activeQstIds, q.questionnaire_id];
                saveQstConfig(ids);
              }}
                className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors border ${activeQstIds.includes(q.questionnaire_id) ? "bg-[#8b5cf6]/10 border-[#8b5cf6]/30 text-white" : "bg-[#0b0811] border-white/5 text-slate-400 hover:border-white/10"}`}
                data-testid={`config-qst-${q.questionnaire_id}`}>
                <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${activeQstIds.includes(q.questionnaire_id) ? "bg-[#8b5cf6] text-white" : "bg-white/5"}`}>
                  {activeQstIds.includes(q.questionnaire_id) && <CheckCircle2 className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{q.name}</div>
                  <div className="text-[9px] text-slate-500">{q.fields?.length || 0} champs, {q.conditions?.length || 0} conditions</div>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("crm.new_prospect")}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Prenom *" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" data-testid="prospect-first-name" />
              <Input placeholder="Nom" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" data-testid="prospect-last-name" />
            </div>
            <Input placeholder="Email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" data-testid="prospect-email" />
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Telephone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
              <Input placeholder="Entreprise" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
            </div>
            <select value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2.5 text-sm">
              {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>

            {/* Questionnaire selection */}
            <div className="border-t border-white/5 pt-3">
              <div className="text-[10px] text-slate-500 uppercase mb-2 font-bold flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Formulaire questionnaire</div>
              <select value={createQstId} onChange={e => { setCreateQstId(e.target.value); setCreateQstResponses({}); }}
                className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm mb-3" data-testid="create-qst-select">
                <option value="">Aucun formulaire</option>
                {allQuestionnaires.filter(q => q.status === "active").map(q => (
                  <option key={q.questionnaire_id} value={q.questionnaire_id}>{q.name} ({q.fields?.length || 0} champs)</option>
                ))}
              </select>
              {createQstId && (() => {
                const qst = allQuestionnaires.find(q => q.questionnaire_id === createQstId);
                if (!qst) return null;
                const sections = {};
                (qst.fields || []).forEach(f => { const sec = f.section || "General"; if (!sections[sec]) sections[sec] = []; sections[sec].push(f); });
                return Object.entries(sections).map(([secName, secFields]) => (
                  <div key={secName} className="mb-3">
                    <div className="text-[9px] text-[#8b5cf6] uppercase font-bold mb-1.5">{secName}</div>
                    <div className="grid grid-cols-2 gap-2">
                      {secFields.map(f => {
                        const fid = f.field_id || f.id;
                        if (!isFieldVisible(qst, fid, createQstResponses)) return null;
                        return (
                          <div key={fid} className={f.type === "textarea" ? "col-span-2" : ""}>
                            <label className="text-[10px] text-slate-500 mb-0.5 block">{f.label} {f.required && <span className="text-red-400">*</span>}</label>
                            {f.type === "select" ? (
                              <select value={createQstResponses[fid] || ""} onChange={e => setCreateQstResponses(r => ({ ...r, [fid]: e.target.value }))}
                                className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs">
                                <option value="">Selectionner...</option>
                                {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : f.type === "textarea" ? (
                              <textarea value={createQstResponses[fid] || ""} onChange={e => setCreateQstResponses(r => ({ ...r, [fid]: e.target.value }))}
                                placeholder={f.placeholder || ""} rows={2} className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs resize-none" />
                            ) : (
                              <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={createQstResponses[fid] || ""} onChange={e => setCreateQstResponses(r => ({ ...r, [fid]: e.target.value }))}
                                placeholder={f.placeholder || ""} className="bg-[#0b0811] border-white/10 text-white text-xs h-8" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>

            <Textarea placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} className="bg-[#0b0811] border-white/10 text-white text-sm" />
            {/* Tags */}
            <div>
              <div className="flex gap-1.5 flex-wrap mb-1.5">
                {form.tags.map((t, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#3b82f6]/10 text-[#3b82f6] flex items-center gap-1">
                    {t} <button onClick={() => removeTag(t)}><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <Input placeholder="Ajouter un tag" value={tagInput} onChange={e => setTagInput(e.target.value)}
                  className="bg-[#0b0811] border-white/10 text-white text-xs flex-1" onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} />
                <Button onClick={addTag} size="sm" variant="outline" className="border-white/10 text-slate-400 text-xs">+</Button>
              </div>
            </div>
            <Button onClick={createProspect} disabled={saving || !form.first_name} className="w-full bg-[#3b82f6] text-white hover:bg-[#2563eb] font-bold" data-testid="confirm-create-prospect">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Creer le prospect
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Meta Lead Ads Dialog */}
      <MetaLeadsDialog open={showMetaLeads} onClose={() => setShowMetaLeads(false)} api={api} onImported={reload} />
      <AircallCallsDialog open={showAircallCalls} onClose={() => setShowAircallCalls(false)} api={api} onImported={reload} />

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle>Importer des prospects</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-slate-400">Importez le fichier CSV exporte depuis Go High Level ou Meta. Les colonnes du questionnaire seront automatiquement mappees.</p>
            <div className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center hover:border-[#3b82f6]/30 transition-colors">
              <Upload className="w-8 h-8 mx-auto mb-2 text-slate-500" />
              <p className="text-sm text-slate-400 mb-3">Glissez un fichier CSV ou cliquez</p>
              <input type="file" accept=".csv,.txt,.tsv" onChange={e => handleImport(e.target.files?.[0])}
                className="block mx-auto text-xs text-slate-400" data-testid="import-file-input" />
            </div>
            <div className="bg-[#0b0811] rounded-lg p-3">
              <div className="text-[10px] text-slate-500 uppercase mb-1">Colonnes attendues (GHL)</div>
              <code className="text-[9px] text-slate-400 block leading-relaxed">Name, Personne (tel), Qualification (email), Action (delai), Statut (pro), created_time</code>
            </div>
            {saving && <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Import en cours...</div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent IA Prospects */}
      {showAgent && (
        <div className="fixed bottom-6 right-6 w-[420px] z-50 shadow-2xl shadow-black/50" data-testid="prospect-agent-panel">
          <AgentChat agentType="crm" agentName="Agent Prospects" agentColor="#3b82f6" onClose={() => setShowAgent(false)} />
        </div>
      )}

      {/* Routines IA Prospects - full width integrated */}
      {showRoutines && (
        <div className="mt-4 mb-2" data-testid="prospect-routines-section">
          <CrmRoutinesPanel onClose={() => setShowRoutines(false)} onRoutineResult={(type, data) => setLastRoutineResult({ type, data, timestamp: new Date().toISOString() })} />
        </div>
      )}
    </div>
  );
}


const ROUTINE_LABELS = {
  prequalification: { title: "Pre-qualification IA", color: "#3b82f6" },
  daily_calls: { title: "Liste d'appels du jour", color: "#22c55e" },
  devis_relance: { title: "Relances devis", color: "#f59e0b" },
};

function RoutineResultClasseur({ result }) {
  const { type, data, timestamp } = result;
  const cfg = ROUTINE_LABELS[type] || { title: type, color: "#f7c935" };
  const text = data?.result || "";

  // Parse the routine text into structured entries
  const entries = parseRoutineEntries(text);

  return (
    <div className="bg-[#14101c] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="routine-classeur-view">
      <div className="px-5 py-3 border-b border-white/[0.06] flex items-center gap-3">
        <Sparkles className="w-4 h-4 text-[#f7c935]" />
        <div className="flex-1">
          <span className="text-sm font-bold text-white">{cfg.title}</span>
          <span className="text-[10px] text-slate-500 ml-2">
            {entries.length} element{entries.length > 1 ? "s" : ""} — genere le {new Date(timestamp).toLocaleString("fr-FR")}
          </span>
        </div>
        <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: cfg.color }} />
      </div>

      {entries.length === 0 ? (
        <div className="p-5">
          <div className="prose-sm text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{text}</div>
        </div>
      ) : (
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-white/[0.06]">
              <th className="px-5 py-2.5 text-[10px] text-[#888194] uppercase font-bold w-8">#</th>
              <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Prospect</th>
              <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Score</th>
              <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Priorite</th>
              <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Raison</th>
              <th className="px-4 py-2.5 text-[10px] text-[#888194] uppercase font-bold">Action recommandee</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => (
              <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`routine-entry-${i}`}>
                <td className="px-5 py-3 text-xs text-slate-500 font-mono">{i + 1}</td>
                <td className="px-4 py-3">
                  <div className="text-sm font-medium text-white">{entry.name}</div>
                  {entry.email && <div className="text-[10px] text-slate-500">{entry.email}</div>}
                </td>
                <td className="px-4 py-3">
                  {entry.score ? (
                    <div className="flex items-center gap-1.5">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black" style={{
                        background: entry.score >= 7 ? "#22c55e20" : entry.score >= 4 ? "#f59e0b20" : "#ef444420",
                        color: entry.score >= 7 ? "#22c55e" : entry.score >= 4 ? "#f59e0b" : "#ef4444",
                      }}>
                        {entry.score}
                      </div>
                      <span className="text-[10px] text-slate-500">/10</span>
                    </div>
                  ) : <span className="text-xs text-slate-500">-</span>}
                </td>
                <td className="px-4 py-3">
                  {entry.priority ? (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                      entry.priority.toLowerCase().includes("haute") ? "bg-red-500/10 text-red-400" :
                      entry.priority.toLowerCase().includes("moyenne") ? "bg-amber-500/10 text-amber-400" :
                      "bg-green-500/10 text-green-400"
                    }`}>
                      {entry.priority}
                    </span>
                  ) : <span className="text-xs text-slate-500">-</span>}
                </td>
                <td className="px-4 py-3 text-[11px] text-slate-400 max-w-[250px]">{entry.reason || "-"}</td>
                <td className="px-4 py-3 text-[11px] text-slate-300 max-w-[250px] font-medium">{entry.action || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function parseRoutineEntries(text) {
  if (!text) return [];
  const entries = [];
  // Split by numbered entries: "1.", "2.", etc.
  const blocks = text.split(/\n\s*\d+\.\s*\*\*/);

  for (let i = 1; i < blocks.length; i++) {
    const block = blocks[i];
    const entry = { name: "", email: "", score: null, priority: "", reason: "", action: "" };

    // Extract name (first line before ** or |)
    const nameMatch = block.match(/^([^*|]+)/);
    if (nameMatch) {
      const parts = nameMatch[1].split("|").map(s => s.trim().replace(/\*\*/g, ""));
      entry.name = parts[0] || "";
      if (parts[1]) entry.email = parts[1];
    }

    // Extract score
    const scoreMatch = block.match(/[Ss]core[^:]*:\s*(\d+)/i);
    if (scoreMatch) entry.score = parseInt(scoreMatch[1]);

    // Extract priority
    const prioMatch = block.match(/[Pp]riorit[ée][^:]*:\s*([^\n]+)/i);
    if (prioMatch) entry.priority = prioMatch[1].trim().replace(/\*\*/g, "");

    // Extract reason
    const reasonMatch = block.match(/[Rr]aison[^:]*:\s*([^\n]+)/i);
    if (reasonMatch) entry.reason = reasonMatch[1].trim().replace(/\*\*/g, "");

    // Extract action
    const actionMatch = block.match(/[Aa]ction[^:]*:\s*([^\n]+)/i);
    if (actionMatch) entry.action = actionMatch[1].trim().replace(/\*\*/g, "");

    if (entry.name) entries.push(entry);
  }

  return entries;
}
